import React from 'react'
import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react'

/**
 * Unified error / empty / loading blocks for the analytics surface.
 *
 * Before: every chart panel shipped its own one-liner (`"Failed to load."`
 * or `"No data for current filters."`). After: a single contract with a
 * headline, actionable hint and consistent visual language across all
 * pages. The blocks size themselves to fit inside the chart body so they
 * don't collapse the card layout.
 */

interface BlockProps {
  minHeight?: number
  className?: string
}

export interface AnalyticsErrorBlockProps extends BlockProps {
  /** Short headline, eg "Failed to load trend". */
  title?: string
  /** Underlying error message (shown under a "Details" toggle). */
  error?: unknown
  /** Called when the retry button is pressed. Hides the button if omitted. */
  onRetry?: () => void
}

function errorMessage(err: unknown): string {
  if (!err) return 'Unknown error'
  if (typeof err === 'string') return err
  if (err instanceof Error) return err.message
  try {
    return JSON.stringify(err)
  } catch {
    return String(err)
  }
}

export function AnalyticsErrorBlock({
  title = 'Failed to load',
  error,
  onRetry,
  minHeight = 120,
  className,
}: AnalyticsErrorBlockProps) {
  const [showDetails, setShowDetails] = React.useState(false)
  return (
    <div
      className={`flex flex-col items-start justify-center gap-2 rounded-md border border-red-100 bg-red-50/60 px-4 py-4 text-left ${className ?? ''}`}
      style={{ minHeight }}
      role="alert"
    >
      <div className="flex items-start gap-2 text-red-700">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div className="text-sm font-medium">{title}</div>
      </div>
      {error != null && (
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          className="text-xs text-red-600 underline decoration-dotted hover:text-red-700"
        >
          {showDetails ? 'Hide details' : 'Show details'}
        </button>
      )}
      {showDetails && error != null && (
        <pre className="max-w-full overflow-auto rounded bg-white/60 px-2 py-1 text-xs text-red-900">
          {errorMessage(error)}
        </pre>
      )}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </button>
      )}
    </div>
  )
}

export interface AnalyticsEmptyStateProps extends BlockProps {
  title?: string
  hint?: string
  /** Optional CTA button. Common use: widen filters. */
  action?: { label: string; onClick: () => void }
}

export function AnalyticsEmptyState({
  title = 'No data for current filters',
  hint = 'Try widening the period or clearing some filters.',
  action,
  minHeight = 120,
  className,
}: AnalyticsEmptyStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-secondary-200 bg-secondary-50/50 px-4 py-6 text-center ${className ?? ''}`}
      style={{ minHeight }}
    >
      <Inbox className="h-5 w-5 text-secondary-400" aria-hidden="true" />
      <div className="text-sm font-medium text-secondary-700">{title}</div>
      {hint && <div className="max-w-sm text-xs text-secondary-500">{hint}</div>}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="mt-1 inline-flex items-center rounded-md border border-secondary-300 bg-white px-3 py-1 text-xs font-medium text-secondary-700 hover:bg-secondary-100"
        >
          {action.label}
        </button>
      )}
    </div>
  )
}

export interface AnalyticsLoadingBlockProps extends BlockProps {
  /** Height hint for the shimmer. Defaults to 320 to match chart height. */
  height?: number
}

export function AnalyticsLoadingBlock({
  height = 320,
  className,
}: AnalyticsLoadingBlockProps) {
  return (
    <div
      className={`animate-pulse rounded-md bg-secondary-100 ${className ?? ''}`}
      style={{ height }}
      aria-busy="true"
      aria-live="polite"
    />
  )
}
